import { reviver } from './session-storage-engine';
import { withoutTransientState } from './transient-state';
import type { PersistKind } from './persist-session-state';

/**
 * ui fields set from the active org/context (via fetchContexts, jumpContext,
 * chooseContext, or the saveServiceLine reducer case) that must not survive
 * an untrusted sessionStorage restore — see restoreSessionState.
 */
export const ORG_CONTEXT_UI_KEYS = [
  'context',
  'contexts',
  'roles',
  'actionGroups',
  'AdminTabs',
  'shortcuts',
  'activeUser',
  'serviceLine',
  'hasAmplifyCaseRunV2Access',
  'hasWorklistMaintenanceWindow',
  'hasResources',
  'bootstrapStatus',
  'awaitingResources',
  'phiMask',
  'caseListColumnConfig',
  'propertyConfig'
] as const;

// hasResources is set by the saveServiceLine reducer case but isn't declared
// in InitialState.ui's literal; its default lives at CMx.tsx's own prop default.
const ORG_CONTEXT_UI_DEFAULTS: Record<string, unknown> = {
  hasResources: false
};

/** Replace org-derived ui fields with InitialState's defaults, on a shallow copy. */
function withoutOrgContextState(
  ui: Record<string, any> | undefined,
  initialUi: Record<string, any>
): Record<string, any> | undefined {
  if (!ui) {
    return ui;
  }
  const sanitized = { ...ui };
  ORG_CONTEXT_UI_KEYS.forEach(key => {
    sanitized[key] = key in initialUi ? initialUi[key] : ORG_CONTEXT_UI_DEFAULTS[key];
  });
  return sanitized;
}

/**
 * Restores a sessionStorage snapshot over InitialState. Org-derived ui
 * fields are only trusted when the snapshot was tagged 'context-switch' —
 * i.e. this same tab's own jumpContext resolved them moments ago and is
 * reloading itself. Any other tag (including missing/legacy snapshots) is
 * untrusted: those fields reset to their initial values so the real
 * GET /contexts/v1 fetch in fetchContexts runs instead of reusing a stale org.
 */
export function restoreSessionState(
  serialisedSavedState: string | null,
  initialState: CMx.ApplicationState
): CMx.ApplicationState {
  if (!serialisedSavedState) {
    return initialState;
  }
  try {
    const savedState = JSON.parse(serialisedSavedState, reviver) as CMx.ApplicationState & {
      router?: unknown;
      __persistMeta?: { kind: PersistKind };
    };
    // Legacy session payloads may still include `router` from connected-react-router;
    // the root reducer no longer has that slice — strip it so combineReducers does not warn.
    const { router: _legacyRouter, __persistMeta, ...savedStateWithoutRouter } = savedState;

    const isTrustedSelfSwitchRestore = __persistMeta?.kind === 'context-switch';
    const restoredUi = isTrustedSelfSwitchRestore
      ? savedStateWithoutRouter.ui
      : withoutOrgContextState(savedStateWithoutRouter.ui, initialState.ui);

    return {
      ...initialState,
      ...withoutTransientState(savedStateWithoutRouter),
      ui: { ...initialState.ui, ...restoredUi },
      cases: { ...initialState.cases, ...savedState.cases }
    };
  } catch (err) {
    console.error('Failed to restore session state; starting fresh.', err);
    return initialState;
  }
}
